import { useEffect, useState } from 'react';
import { Allocation } from './MemberAllocations';

export type RemovedItem = { clientId: string; itemId: string };

type Seed = { clientId: string; itemId?: string; amounts: Record<string, number | null> };

/**
 * Who a cashflow item applies to and each member's own figures. Members dropped while editing
 * are remembered so their stored row can be deleted rather than left orphaned.
 */
export function useAllocations(
  opened: boolean,
  seed: Seed[] | undefined,
  defaultClientId?: string,
) {
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [removedItems, setRemovedItems] = useState<RemovedItem[]>([]);

  useEffect(() => {
    if (!opened) return;
    setRemovedItems([]);
    if (seed && seed.length > 0) {
      // An amount not recorded opens as an empty field, not as zero
      setAllocations(
        seed.map(entry => ({
          ...entry,
          amounts: Object.fromEntries(
            Object.entries(entry.amounts).map(([key, value]) => [key, value ?? '']),
          ),
        })),
      );
      return;
    }
    setAllocations(defaultClientId ? [{ clientId: defaultClientId, amounts: {} }] : []);
    // `seed` is rebuilt on every render by its caller, so it cannot be a dependency without
    // resetting the form on each keystroke; opening the modal is what should reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultClientId]);

  const addMember = (clientId: string) =>
    setAllocations(current => [...current, { clientId, amounts: {} }]);

  const removeMember = (clientId: string) => {
    const dropped = allocations.find(allocation => allocation.clientId === clientId);
    if (dropped?.itemId) {
      setRemovedItems(current => [...current, { clientId, itemId: dropped.itemId as string }]);
    }
    setAllocations(current => current.filter(allocation => allocation.clientId !== clientId));
  };

  const setAmount = (clientId: string, key: string, value: number | '') =>
    setAllocations(current =>
      current.map(allocation =>
        allocation.clientId === clientId
          ? { ...allocation, amounts: { ...allocation.amounts, [key]: value } }
          : allocation,
      ),
    );

  return { allocations, removedItems, addMember, removeMember, setAmount };
}
